import { useEffect, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { Box, styled } from '@mui/material';

import { configApi } from 'Api';
import { AlertSuccess } from 'Common/AlertSuccess';
import { EmailField, FormButtons, SelectField, TextField } from 'Common/Form';
import { SaveCurrentUserInfoSchema } from 'Common/Form/ValidationSchemas';
import { Avatar } from 'Common/UserAvatar/Avatar';
import { AvatarConsts } from 'Common/UserAvatar/Avatar/Avatar.consts';
import { DEFAULT_TIMEZONE } from 'Consts/timezone';
import { useAppDispatch } from 'Store';
import { Spacing } from 'Themes/spacing';
import { setTimezone, useSelectUserTimezone } from 'User/data';
import { useAppForm, useBoolean } from 'Utils/customHooks';

import type { ISaveCurrentUserInfoRequest } from '@libs/models';

import { ColorPicker } from './ColorPicker';
import { formFieldNames } from './formFieldNames';

import type { TimezoneModel } from 'Api';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.medium};
`;

const AvatarContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

type Props = {
  onSubmit: (formData: ISaveCurrentUserInfoRequest) => void;
  onCancel?: () => void;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  avatarColor?: string | null;
  timezone?: string | null;
  isSubmitSuccess?: boolean;
};

export const MyAccountForm = ({
  onSubmit,
  onCancel,
  email,
  firstName,
  lastName,
  displayName,
  avatarColor,
  timezone,
  isSubmitSuccess
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preferencesTimezone = useSelectUserTimezone();

  const [timezones, setTimezones] = useState<TimezoneModel[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);
  const [timezonesError, setTimezonesError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      setTimezonesError(null);

      try {
        const result = await configApi.getTimezones();

        if (result.isSuccess && result.data) {
          setTimezones(result.data.timezones);
        } else {
          setTimezonesError(result.error ?? 'Failed to load timezones');
        }
      } catch {
        setTimezonesError('Failed to load timezones');
      } finally {
        setIsLoadingTimezones(false);
      }
    };

    fetchTimezones();
  }, []);

  const formFieldDefaultValues = {
    [formFieldNames.email]: email,
    [formFieldNames.firstName]: firstName,
    [formFieldNames.lastName]: lastName,
    [formFieldNames.displayName]: displayName,
    [formFieldNames.avatarColor]: avatarColor,
    [formFieldNames.timezone]: timezone ?? preferencesTimezone ?? DEFAULT_TIMEZONE
  };

  const formContext = useAppForm<ISaveCurrentUserInfoRequest>(
    SaveCurrentUserInfoSchema(),
    formFieldDefaultValues
  );

  const [colorPickerIsDisplayed, displayColorPicker, hideColorPicker] = useBoolean();

  const firstNameValue = formContext.watch(formFieldNames.firstName);
  const displayNameValue = formContext.watch(formFieldNames.displayName);
  const colorValue = formContext.watch(formFieldNames.avatarColor);
  const timezoneValue = formContext.watch(formFieldNames.timezone);

  const userDisplayName = firstNameValue || displayNameValue || AvatarConsts.fallbackUserName;

  const handleColorClick = (value: string) => {
    formContext.setValue(formFieldNames.avatarColor, value);
    hideColorPicker();
  };

  const handleOnSubmit = (formData: ISaveCurrentUserInfoRequest) => {
    formData.avatarColor = colorValue;

    if (timezoneValue && timezoneValue !== preferencesTimezone) {
      dispatch(setTimezone(timezoneValue));
    }

    onSubmit(formData);
  };

  const timezoneOptions = timezones.map((tz: TimezoneModel) => ({
    id: tz.id,
    label: tz.label
  }));

  return (
    <FormContainer onSuccess={handleOnSubmit} formContext={formContext}>
      <Container>
        {isSubmitSuccess && <AlertSuccess text={t('myAccount.changesHaveBeenSaved')} />}

        {!colorPickerIsDisplayed ? (
          <>
            <EmailField name={formFieldNames.email} disabled label={t('myAccount.loginEmail')} />

            <TextField name={formFieldNames.firstName} label={t('myAccount.firstName')} required />

            <TextField name={formFieldNames.lastName} label={t('myAccount.lastName')} required />

            <TextField
              name={formFieldNames.displayName}
              label={t('myAccount.displayName')}
              required
            />

            {isLoadingTimezones ? (
              <div>{t('loadingTimezones')}</div>
            ) : timezonesError ? (
              <div style={{ color: 'red' }}>
                {t('errorLoadingTimezones')}: {timezonesError}
              </div>
            ) : (
              <SelectField
                name={formFieldNames.timezone}
                label={t('myAccount.timezone')}
                options={timezoneOptions}
                required
                slotProps={{
                  select: {
                    MenuProps: {
                      style: { zIndex: 1600 },
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 1600
                        }
                      }
                    }
                  }
                }}
              />
            )}

            <AvatarContainer onClick={displayColorPicker}>
              <Avatar color={colorValue || ''} displayName={userDisplayName} shouldBiggerOnHover />
              <span>{t('myAccount.avatarColor')}</span>
            </AvatarContainer>
          </>
        ) : (
          <ColorPicker displayName={userDisplayName} onColorClick={handleColorClick} />
        )}

        <FormButtons
          onSubmit={() => {}}
          onCancel={onCancel}
          submitText={t('Save')}
          cancelText={t('Cancel')}
          showCancel={!!onCancel}
        />
      </Container>
    </FormContainer>
  );
};
